import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { alpha } from '@mui/material/styles';
import { useOnboardingProgress } from '@/features/onboarding/hooks/useOnboardingProgress';
import { useGuideBookings } from '@/features/guide/hooks/useGuideBookings';
import { useGuidePricing } from '@/features/guide/hooks/useGuidePricing';
import { GuideStatCard } from '@/features/guide/components/GuideStatCard';
import { BookingCard } from '@/features/guide/components/BookingCard';
import { GuideGoLiveSetupBanner } from '@/features/guide/components/GuideGoLiveSetupBanner';
import { DashboardSectionHeader } from '@/shared/components/dashboard/DashboardSectionHeader';
import { GuideExtensionRequests } from '@/features/trip-extension';
import { BookingStatus } from '@/types';
import { GUIDE_ROUTES } from '@/features/guide/constants/routes';
import { isGuideMarketplaceLive } from '@/features/guide/utils/pricing';
import { dashboardLightHeroSx, dashboardSectionPanelSx } from '@/shared/styles/surfaces';
import { RewardsTeaserCard } from '@/features/guide-rewards/components/RewardsTeaserCard';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function GuideHomePage() {
  const navigate = useNavigate();

  const { data: progress, isLoading: profileLoading } = useOnboardingProgress();

  const { data: recentBookings, isLoading: recentLoading } = useGuideBookings({
    page: 1,
    limit: 3,
    status: BookingStatus.CONFIRMED,
  });

  const { data: pricing, isLoading: pricingLoading } = useGuidePricing();

  const profile = progress?.profile;
  const fullName = profile?.fullName?.trim();
  const firstName = fullName?.split(' ')[0];
  const profilePhoto = profile?.profilePhoto?.trim() || undefined;
  const upcomingCount = recentBookings?.pagination?.total ?? 0;
  const pendingCount: number = 0;
  const uploadedDocumentsCount = progress?.documents?.length ?? 0;
  const reviewCount = profile?.reviewCount ?? 0;
  const averageRating = reviewCount > 0 && typeof profile?.rating === 'number' ? profile.rating.toFixed(1) : '—';
  const locationLabel = [profile?.city, profile?.state].filter(Boolean).join(', ');
  const isLive = isGuideMarketplaceLive(pricing);
  const showGoLiveSetup = !pricingLoading && !isLive;

  return (
    <Box>
      {showGoLiveSetup && (
        <Box sx={{ mb: 3 }}>
          <GuideGoLiveSetupBanner />
        </Box>
      )}

      {/* Hero + rewards teaser: side by side on wide screens, stacked below. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1.5fr) minmax(0, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <Paper elevation={0} sx={{ ...dashboardLightHeroSx, p: { xs: 3, md: 4.5 }, minHeight: { lg: 300 }, display: 'flex' }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'flex-start' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                  minWidth: 0,
                }}
              >
                {profileLoading ? (
                  <Skeleton variant="circular" width={84} height={84} sx={{ bgcolor: 'rgba(26,46,26,0.08)' }} />
                ) : (
                  <Avatar
                    src={profilePhoto}
                    sx={{
                      width: 84,
                      height: 84,
                      border: '3px solid rgba(255,255,255,0.95)',
                      boxShadow: '0 14px 28px rgba(15,23,42,0.12)',
                    }}
                  >
                    {firstName?.[0]?.toUpperCase() ?? 'G'}
                  </Avatar>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'secondary.main',
                      letterSpacing: '0.14em',
                      fontWeight: 700,
                    }}
                  >
                    {getGreeting()}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.15,
                      mt: 0.25,
                      color: 'text.primary',
                    }}
                  >
                    {profileLoading ? (
                      <Skeleton width={240} sx={{ bgcolor: 'rgba(26,46,26,0.08)' }} />
                    ) : (
                      <>Welcome back{firstName ? `, ${firstName}` : ''}</>
                    )}
                  </Typography>
                  {locationLabel && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Hosting in {locationLabel}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {pricingLoading ? (
                  <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: 99 }} />
                ) : (
                  <Chip
                    label={isLive ? 'Live listing' : 'Add pricing to go live'}
                    size="small"
                    sx={(theme) =>
                      isLive
                        ? {
                            bgcolor: 'rgba(26,46,26,0.06)',
                            color: 'primary.main',
                            border: '1px solid rgba(26,46,26,0.12)',
                            fontWeight: 600,
                          }
                        : {
                            bgcolor: alpha(theme.palette.secondary.main, 0.14),
                            color: 'secondary.dark',
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                            fontWeight: 700,
                          }
                    }
                  />
                )}
                {pendingCount > 0 && (
                  <Button
                    variant="contained"
                    color="secondary"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(GUIDE_ROUTES.BOOKINGS)}
                    sx={{
                      borderRadius: 99,
                      fontWeight: 700,
                      px: 2.5,
                      boxShadow: '0 10px 24px rgba(15,23,42,0.12)',
                    }}
                  >
                    {pendingCount} pending payment
                    {pendingCount !== 1 ? 's' : ''}
                  </Button>
                )}
              </Box>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 'auto', pt: 2.5, maxWidth: 560, lineHeight: 1.7 }}
            >
              {showGoLiveSetup
                ? 'Set your rates to appear in traveler search and start getting bookings.'
                : 'Manage bookings, pricing, and availability — everything you need to host unforgettable experiences.'}
            </Typography>

            <Box
              sx={{
                mt: 2.5,
                pt: 2.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(26,46,26,0.06)',
                    color: 'primary.main',
                  }}
                >
                  <BadgeOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    My Trplin Identity
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Share your verified Guide ID and printable credential with travelers.
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(GUIDE_ROUTES.IDENTITY)}
                sx={{ fontWeight: 700, flexShrink: 0, borderRadius: 99, px: 2.5 }}
              >
                View identity
              </Button>
            </Box>
          </Box>
        </Paper>

        <RewardsTeaserCard />
      </Box>

      {/* Stats */}
      <DashboardSectionHeader title="At a glance" subtitle="Your hosting performance this season" />
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <GuideStatCard
            label="Upcoming"
            value={upcomingCount}
            loading={recentLoading}
            icon={<EventNoteIcon />}
            accent="warning.main"
            onClick={() => navigate(GUIDE_ROUTES.BOOKINGS)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <GuideStatCard
            label="Profile status"
            value={profile?.status ?? '—'}
            loading={profileLoading}
            icon={<RateReviewIcon />}
            accent="success.main"
            subtitle="Current onboarding status"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <GuideStatCard
            label="Documents"
            value={uploadedDocumentsCount}
            loading={profileLoading}
            icon={<EventAvailableIcon />}
            accent="primary.main"
            subtitle="Uploaded for verification"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <GuideStatCard
            label="Rating"
            value={averageRating}
            loading={profileLoading}
            icon={<RateReviewIcon />}
            accent="secondary.main"
            subtitle={reviewCount ? `${reviewCount} reviews` : 'No reviews yet'}
            onClick={() => navigate(GUIDE_ROUTES.REVIEWS)}
          />
        </Grid>
      </Grid>

      <GuideExtensionRequests />

      {/* Recent bookings */}
      <Paper elevation={0} sx={{ ...dashboardSectionPanelSx }}>
        <DashboardSectionHeader
          title="Upcoming bookings"
          subtitle="Confirmed trips on your calendar"
          action={
            <Button
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(GUIDE_ROUTES.BOOKINGS)}
              sx={{ fontWeight: 600 }}
            >
              View all
            </Button>
          }
        />

        {recentLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={108} sx={{ borderRadius: 'var(--radius-lg)' }} />
            ))}
          </Box>
        ) : !recentBookings?.data?.length ? (
          <Box
            sx={{
              py: 5,
              px: 3,
              textAlign: 'center',
              borderRadius: 'var(--radius-md)',
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'primary.50',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <EventBusyOutlinedIcon />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              No bookings yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto' }}>
              {showGoLiveSetup
                ? 'Add pricing to go live — then confirmed trips will show up here.'
                : 'When travelers book and pay, confirmed trips will show up here.'}
            </Typography>
          </Box>
        ) : (
          recentBookings.data.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              onClick={() => navigate(GUIDE_ROUTES.bookingDetail(booking.id))}
            />
          ))
        )}
      </Paper>
    </Box>
  );
}
